import { useState, useCallback, useRef, useEffect } from 'react';
import type { Message, MessageType, PreviewMoveResultPayload } from '@sheshdash/shared';

// ADR-002 §5: client SHOULD debounce hover ~150 ms (no point firing for
// fly-by cursors), apply a 5-second receive timeout, and drop all
// in-flight correlations on WS reconnect. Long-press timing for touch
// (~250 ms) is the caller's responsibility — this hook just exposes
// requestPreview/cancelPreview, the trigger gesture is up to the UI.
const HOVER_DEBOUNCE_MS = 150;
const RESPONSE_TIMEOUT_MS = 5000;

// Vite inlines `import.meta.env.VITE_*` at build time — read once.
const USE_PREVIEW_MOVE_ENV: boolean =
  import.meta.env?.VITE_USE_PREVIEW_MOVE === 'true';

export type PreviewMoveState =
  | { status: 'pending'; request_id: string }
  | { status: 'ok'; result: PreviewMoveResultPayload }
  | { status: 'error'; message: string };

interface UsePreviewMoveOptions {
  sendMessage: (type: MessageType, payload: unknown) => void;
  isConnected: boolean;
  /** Override the env-baked default. Tests pass `true` regardless of the env. */
  enabled?: boolean;
}

export interface UsePreviewMoveReturn {
  /** Per-piece latest preview state. Keys are piece IDs. */
  previewByPiece: Record<string, PreviewMoveState>;
  /** Schedule a debounced PREVIEW_MOVE for `pieceId`. Idempotent if a
   *  request for the same piece is already in-flight. No-op when the
   *  feature flag is off, when the WS is not connected, or while
   *  unmounted. */
  requestPreview: (pieceId: string) => void;
  /** Cancel a *pending* (debounced) request that hasn't been sent yet.
   *  Does NOT cancel in-flight requests — the response will arrive and
   *  populate `previewByPiece`; the UI is responsible for ignoring it
   *  if the user moved on. */
  cancelPreview: () => void;
  /** Caller (the WS owner) MUST invoke this for every incoming Message
   *  so the hook can correlate `PREVIEW_MOVE_RESULT` and request-tagged
   *  `ERROR` responses. Other message types are ignored. */
  consumeMessage: (msg: Message) => void;
  /** Clear all state — debounce, in-flight map, response timeouts,
   *  preview cache. Must be called on WS reconnect (`onOpen`) and on
   *  game leave so stale responses don't bleed across sessions. */
  flushAll: () => void;
  /** Resolved feature-flag value the hook is using. */
  enabled: boolean;
}

export function usePreviewMove({
  sendMessage,
  isConnected,
  enabled = USE_PREVIEW_MOVE_ENV,
}: UsePreviewMoveOptions): UsePreviewMoveReturn {
  const [previewByPiece, setPreviewByPiece] = useState<Record<string, PreviewMoveState>>({});
  const inflightRef = useRef<Map<string, string>>(new Map()); // request_id → piece_id
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responseTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pendingPieceRef = useRef<string | null>(null);

  const flushAll = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    for (const t of responseTimersRef.current.values()) clearTimeout(t);
    responseTimersRef.current.clear();
    inflightRef.current.clear();
    pendingPieceRef.current = null;
    setPreviewByPiece({});
  }, []);

  // ADR-002 §5: drop all in-flight correlations on WS reconnect. We watch
  // the disconnected→connected transition; when isConnected flips false
  // (i.e., we lost the socket) we wipe state immediately so a late
  // PREVIEW_MOVE_RESULT carrying a now-stale request_id is silently
  // dropped by consumeMessage (the request_id won't be in inflightRef).
  const wasConnectedRef = useRef<boolean>(isConnected);
  useEffect(() => {
    if (wasConnectedRef.current && !isConnected) {
      flushAll();
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected, flushAll]);

  // Cleanup on unmount.
  useEffect(() => () => flushAll(), [flushAll]);

  const requestPreview = useCallback((pieceId: string) => {
    if (!enabled || !isConnected) return;
    pendingPieceRef.current = pieceId;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const piece = pendingPieceRef.current;
      if (!piece) return;
      pendingPieceRef.current = null;
      // Dedup: if a request for this exact piece is still in-flight, skip.
      // The user typically hovers, looks away, hovers again — we don't
      // want to spam the server with redundant PREVIEW_MOVEs for the
      // same piece while we're still waiting on the first answer.
      for (const inFlightPiece of inflightRef.current.values()) {
        if (inFlightPiece === piece) return;
      }
      const request_id = generateRequestId();
      inflightRef.current.set(request_id, piece);
      setPreviewByPiece((prev) => ({ ...prev, [piece]: { status: 'pending', request_id } }));
      sendMessage('PREVIEW_MOVE', { request_id, piece_id: piece });
      const timer = setTimeout(() => {
        // ADR-002 §5: timeout drops the in-flight correlation, no auto-retry.
        inflightRef.current.delete(request_id);
        responseTimersRef.current.delete(request_id);
        setPreviewByPiece((prev) => {
          const cur = prev[piece];
          if (cur?.status === 'pending' && cur.request_id === request_id) {
            const next = { ...prev };
            delete next[piece];
            return next;
          }
          return prev;
        });
      }, RESPONSE_TIMEOUT_MS);
      responseTimersRef.current.set(request_id, timer);
    }, HOVER_DEBOUNCE_MS);
  }, [enabled, isConnected, sendMessage]);

  const cancelPreview = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingPieceRef.current = null;
  }, []);

  const consumeMessage = useCallback((msg: Message) => {
    if (msg.type === 'PREVIEW_MOVE_RESULT') {
      const payload = msg.payload as PreviewMoveResultPayload;
      const pieceId = inflightRef.current.get(payload.request_id);
      if (!pieceId) return; // unknown / stale (post-reconnect or post-timeout)
      inflightRef.current.delete(payload.request_id);
      const t = responseTimersRef.current.get(payload.request_id);
      if (t) {
        clearTimeout(t);
        responseTimersRef.current.delete(payload.request_id);
      }
      setPreviewByPiece((prev) => ({ ...prev, [pieceId]: { status: 'ok', result: payload } }));
      return;
    }
    if (msg.type === 'ERROR') {
      const payload = msg.payload as { message: string; request_id?: string };
      // Only consume request-tagged errors — global errors are handled by
      // useGameState. ADR-002 §1: optional request_id distinguishes the two.
      if (!payload.request_id) return;
      const pieceId = inflightRef.current.get(payload.request_id);
      if (!pieceId) return;
      inflightRef.current.delete(payload.request_id);
      const t = responseTimersRef.current.get(payload.request_id);
      if (t) {
        clearTimeout(t);
        responseTimersRef.current.delete(payload.request_id);
      }
      setPreviewByPiece((prev) => ({ ...prev, [pieceId]: { status: 'error', message: payload.message } }));
    }
  }, []);

  return { previewByPiece, requestPreview, cancelPreview, consumeMessage, flushAll, enabled };
}

/** crypto.randomUUID() is on every modern browser since 2021 and on
 *  Node ≥ 19 (vitest), but jsdom in older toolchains doesn't expose it.
 *  Fallback to a non-cryptographic v4-ish string — request_id is purely
 *  for client-side correlation, the server never gates on its uniqueness
 *  beyond echoing it back, so this is safe. */
function generateRequestId(): string {
  const c: Crypto | undefined =
    typeof globalThis !== 'undefined' && 'crypto' in globalThis
      ? (globalThis.crypto as Crypto | undefined)
      : undefined;
  if (c?.randomUUID) return c.randomUUID();
  const rnd = (n: number) => Math.floor(Math.random() * n).toString(16).padStart(2, '0');
  return `${rnd(255)}${rnd(255)}${rnd(255)}${rnd(255)}-${rnd(255)}${rnd(255)}-4${rnd(15).slice(-1)}${rnd(255).slice(-2)}-${rnd(255)}${rnd(255)}-${rnd(255)}${rnd(255)}${rnd(255)}${rnd(255)}${rnd(255)}${rnd(255)}`;
}
